/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { PERMISSION_HIERARCHY } from "./permissions.types";
import type { AgencyMember, AgencyWithMembers, PermissionAction, UserRole } from "./permissions.types";

export type { PermissionAction, UserRole } from "./permissions.types";
export { PERMISSION_HIERARCHY } from "./permissions.types";

/**
 * Roles each role inherits from, lowest first. PERMISSION_HIERARCHY lists only
 * what a role ADDS on top of the ones below it, so resolving an effective
 * permission set means walking this chain — a direct lookup would strip
 * `admin` of the property/visit actions it inherits from `broker`.
 */
const ROLE_INHERITANCE: Record<UserRole, UserRole[]> = {
  anonymus: ["anonymus"],
  broker: ["anonymus", "broker"],
  operational: ["anonymus", "broker", "operational"],
  admin: ["anonymus", "broker", "operational", "admin"],
  owner: ["anonymus", "broker", "operational", "admin", "owner"],
};

/**
 * Gets the role of the current user in the agency
 * @param agency - The agency object containing members
 * @param userEmail - The email of the current user
 * @returns The role of the user ('admin', 'broker', 'operational', 'anonymus') or null if not found
 */
// Helper function to check if agency and user email are valid
function isValidAgencyAndUser(
  agency: AgencyWithMembers | null | undefined,
  userEmail: string | null | undefined,
): boolean {
  return !!(agency && userEmail && agency.members);
}

// Helper function to find member by email
/**
 * findMemberByEmail function.
 *
 * @param {*} members - Parameter description
 * @param {*} userEmail - Parameter description
 *
 * @returns {*} Return value description
 */
function findMemberByEmail(members: any[], userEmail: string): any {
  return members.find(
    (member) => member.email?.toLowerCase() === userEmail.toLowerCase()
  );
}

// Helper function to validate and return role
/**
 * validateRole function.
 *
 * @param {*} role - Parameter description
 *
 * @returns {*} Return value description
 */
function validateRole(role: string | undefined): UserRole {
  const normalizedRole = role?.toLowerCase();
  const validRoles = ["owner", "admin", "operational", "broker"];
  if (normalizedRole && validRoles.includes(normalizedRole)) {
    return normalizedRole as UserRole;
  }
  return "anonymus";
}

/**
 * getCurrentUserRole function.
 *
 * @param {*} agency - Parameter description
 * @param {*} userEmail - Parameter description
 *
 * @returns {*} Return value description
 */
export const getCurrentUserRole = (
  agency: AgencyWithMembers | null | undefined,
  userEmail: string | null | undefined
): UserRole => {
  if (!isValidAgencyAndUser(agency, userEmail)) {
    return "anonymus";
  }

  const member = findMemberByEmail(agency!.members!, userEmail!);
  if (!member) {
    return "anonymus";
  }

  return validateRole(member.role);
};

/**
 * Checks if the current user is an admin in the agency
 * @param role - The role of the current user
 * @returns true if the user is an admin, false otherwise
 */
export const isAdmin = (
  role: UserRole | null | undefined,
): boolean => {
  return role === "admin" || role === "owner";
};

/**
 * Checks if the current user is operational or higher
 * @param role - The role of the current user
 * @returns true if the user is operational or admin, false otherwise
 */
export const isOperationalOrHigher = (
  role: UserRole | null | undefined,
): boolean => {
  return role ? ["owner", "admin", "operational"].includes(role) : false;
};

/**
 * Checks if the current user is broker or higher
 * @param role - The role of the current user
 * @returns true if the user is broker, operational or admin, false otherwise
 */
export const isBrokerOrHigher = (
  role: UserRole | null | undefined,
): boolean => {
  return role ? ["owner", "admin", "operational", "broker"].includes(role) : false;
};

/**
 * Resolves the full set of actions a role can perform, accumulating every
 * action declared by the role itself and by the roles it inherits from.
 *
 * @param role - The role to resolve.
 * @returns The effective permission set for the role.
 */
export const resolvePermissions = (role: UserRole): PermissionAction[] => {
  return (ROLE_INHERITANCE[role] ?? []).flatMap((inherited) => PERMISSION_HIERARCHY[inherited] ?? []);
};

/**
 * Checks if the user has permission to perform a specific action
 * @param action - The action to check permission for
 * @param role - The role of the current user
 * @returns true if the user has permission, false otherwise
 */
export const hasPermission = (
  action: PermissionAction,
  role: UserRole | null | undefined
): boolean => {
  if (!role || role === "anonymus") return false;
  return resolvePermissions(role).includes(action);
};







/** A place the subscription pays for. */
const PLAN_SEAT = "plan";

/** A free place an auxiliary user occupies. */
const SUPPORT_SEAT = "support";

/**
 * Which kind of place a membership holds, with silence read as paid.
 *
 * Anything the vocabulary does not know is read as paid too. A typo must
 * never be what turns a billed seat into a free one, and the mistake that
 * fails closed here is the safe one: the person sees a module they could
 * not use and the backend refuses it, rather than losing access they paid
 * for.
 *
 * @param member - The membership row, or null when the person is not on this team.
 * @returns `"support"` or `"plan"`.
 */
export const seatKindOf = (member: AgencyMember | null | undefined): string => {
  const raw = member?.seat_kind;
  if (typeof raw !== "string") return PLAN_SEAT;
  return raw.trim().toLowerCase() === SUPPORT_SEAT ? SUPPORT_SEAT : PLAN_SEAT;
};

/**
 * Finds the signed-in person's membership in this agency.
 *
 * @param agency - The agency payload, which carries its own roster.
 * @param userEmail - The signed-in address.
 * @returns The membership row, or null when they are not on this team.
 */
export const memberOf = (
  agency: AgencyWithMembers | null | undefined,
  userEmail: string | null | undefined,
): AgencyMember | null => {
  const wanted = (userEmail ?? "").trim().toLowerCase();
  if (!wanted) return null;
  const members = agency?.members ?? [];
  return members.find((m) => (m?.email ?? "").trim().toLowerCase() === wanted) ?? null;
};

/**
 * Whether this person may open this module in this agency.
 *
 * The second axis of access, and it does not collapse into `role`: an
 * auxiliary user is an `operational` with every permission an `operational`
 * has. What sets them apart is that nothing pays for their place, so the
 * modules they do not open are exactly the two the catalogue meters per paid
 * seat — the AI and partnerships.
 *
 * The list comes from the agency payload rather than from a constant here.
 * When it is missing — an older backend, a payload still loading — nothing
 * is blocked: the backend answers 403 either way, and guessing a blocklist
 * on this side would hide modules from people who are paying for them.
 *
 * @param moduleId - The module's id, as the nav registry spells it.
 * @param agency - The agency payload, for the roster and the blocked list.
 * @param userEmail - The signed-in address.
 * @returns true when the module is theirs to open.
 */
export const canUseModule = (
  moduleId: string,
  agency: AgencyWithMembers | null | undefined,
  userEmail: string | null | undefined,
): boolean => {
  const blocked = agency?.support_blocked_modules ?? [];
  if (!blocked.includes(moduleId)) return true;
  return seatKindOf(memberOf(agency, userEmail)) !== SUPPORT_SEAT;
};
